export interface Category {
  id: string;
  name: string;
  level?: number;
  parentId?: string;
  children?: Category[];
}

class CategoryService {
  // Inicijalizuj kao prazan niz umesto null
  private categories: Category[] = [];
  private flatCategories: Category[] = [];

  /**
   * Dohvata hijerarhijske kategorije iz JSON fajla
   */
  async getCategories(): Promise<Category[]> {
    // Ako već imamo kategorije, vrati ih
    if (this.categories.length > 0) {
      return this.categories;
    }

    try {
      // Učitavanje JSON fajla iz public direktorijuma
      const response = await fetch('/categories.json');
      const data = await response.json();
      this.categories = data.categories;

      // Pripremi ravnu listu svih kategorija za lakše pretraživanje
      this.prepareFlatCategories();

      return this.categories;
    } catch (error) {
      console.error('Failed to load categories:', error);

      // Fallback na hardkodovane kategorije ako JSON nije dostupan
      this.categories = this.getFallbackCategories();
      this.prepareFlatCategories();
      return this.categories;
    }
  }

  /**
   * Dohvata ravan niz svih kategorija (glavne + podkategorije)
   */
  async getAllFlatCategories(): Promise<Category[]> {
    if (this.flatCategories.length === 0) {
      await this.getCategories(); // ovo će napuniti i flatCategories
    }
    return this.flatCategories;
  }

  /**
   * Priprema ravan niz svih kategorija iz hijerarhijske strukture
   */
  private prepareFlatCategories(): void {
    this.flatCategories = [];

    // Dodaj sve glavne kategorije
    this.categories.forEach((category) => {
      this.flatCategories.push({
        id: category.id,
        name: category.name,
        level: category.level,
        parentId: category.parentId,
      });

      // Dodaj sve podkategorije
      if (category.children && category.children.length > 0) {
        category.children.forEach((child) => {
          this.flatCategories.push({
            id: child.id,
            name: child.name,
            level: child.level,
            parentId: child.parentId || category.id, // Osiguraj da podkategorija uvek ima parentId
          });
        });
      }
    });
  }

  /**
   * Pronalazi i vraća kategoriju po ID-u
   */
  async getCategoryById(id: string): Promise<Category | undefined> {
    if (this.flatCategories.length === 0) {
      await this.getCategories();
    }
    return this.flatCategories.find((category) => category.id === id);
  }

  /**
   * Vraća naziv kategorije po ID-u
   */
  async getCategoryName(id: string): Promise<string> {
    const category = await this.getCategoryById(id);
    return category ? category.name : 'Nepoznata kategorija';
  }

  /**
   * Vraća punu putanju kategorije (uključujući roditeljsku)
   */
  async getCategoryPath(id: string): Promise<string> {
    const category = await this.getCategoryById(id);
    if (!category) return 'Nepoznata kategorija';

    if (!category.parentId) {
      return category.name;
    }

    // Pronađi roditeljsku kategoriju
    const parent = await this.getCategoryById(category.parentId);
    if (!parent) return category.name;

    return `${parent.name} > ${category.name}`;
  }

  /**
   * Vraća hardkodovane kategorije kao rezervnu opciju
   */
  private getFallbackCategories(): Category[] {
    return [
      {
        id: '1',
        name: 'Elektronika',
        level: 1,
        children: [
          {
            id: '1-1',
            name: 'Mobilni telefoni',
            level: 2,
            parentId: '1',
          },
          {
            id: '1-2',
            name: 'Računari',
            level: 2,
            parentId: '1',
          },
        ],
      },
      {
        id: '2',
        name: 'Odeća i obuća',
        level: 1,
        children: [
          {
            id: '2-1',
            name: 'Muška odeća',
            level: 2,
            parentId: '2',
          },
          {
            id: '2-2',
            name: 'Ženska odeća',
            level: 2,
            parentId: '2',
          },
        ],
      },
      {
        id: '3',
        name: 'Nameštaj',
        level: 1,
        children: [
          {
            id: '3-1',
            name: 'Kuhinjski nameštaj',
            level: 2,
            parentId: '3',
          },
          {
            id: '3-2',
            name: 'Dnevna soba',
            level: 2,
            parentId: '3',
          },
        ],
      },
    ];
  }

  /**
   * Proverava da li je kategorija podkategorija (ima roditeljsku kategoriju)
   */
  async isSubcategory(categoryId: string): Promise<boolean> {
    const category = await this.getCategoryById(categoryId);
    return category?.parentId !== undefined && category.parentId !== null;
  }

  /**
   * Proverava da li je kategorija glavna kategorija (nema roditeljsku kategoriju)
   */
  async isMainCategory(categoryId: string): Promise<boolean> {
    const category = await this.getCategoryById(categoryId);
    return (
      category?.level === 1 ||
      (category?.parentId === undefined && category?.parentId === null)
    );
  }

  /**
   * Vraća samo podkategorije (kategorije koje imaju parentId)
   */
  async getSubcategories(): Promise<Category[]> {
    const allCategories = await this.getAllFlatCategories();
    return allCategories.filter(
      (cat) => cat.parentId !== undefined && cat.parentId !== null
    );
  }

  /**
   * Vraća samo glavne kategorije (kategorije koje nemaju parentId)
   */
  async getMainCategories(): Promise<Category[]> {
    const allCategories = await this.getAllFlatCategories();
    return allCategories.filter(
      (cat) =>
        cat.level === 1 || (cat.parentId === undefined && cat.parentId === null)
    );
  }

  /**
   * Resetuje kategorije i primorava ponovno učitavanje
   */
  reset(): void {
    this.categories = [];
    this.flatCategories = [];
  }
}

// Kreiramo singleton instancu
const categoryService = new CategoryService();
export default categoryService;
